import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Ban, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, Mail, MailCheck, RefreshCw, RotateCcw,
  Pencil, Save, Send, Settings as SettingsIcon, SkipForward, Trash2, Clock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader, { ghostBtn, card } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Same look as the CRM panel (components/crm/CrmPanel.jsx).
const field = "border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan";
const selectCls = "h-10 rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";
const label = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const btnPrimary = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";
const rowBtn = "flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-300 transition-colors hover:border-vortex-cyan/60 hover:text-vortex-cyan disabled:opacity-50";

const Pill = ({ children, className = "" }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] tracking-[0.12em] uppercase ${className}`}>
    {children}
  </span>
);

const STEP_LABEL = { 1: "Intro", 2: "Follow-up", 3: "Final note" };
const STEP_TONE = { 1: "border-slate-400/50 text-slate-300", 2: "border-sky-400/50 text-sky-300", 3: "border-amber-400/50 text-amber-300" };
const TABS = [
  { key: "queue", label: "Due today" },
  { key: "templates", label: "Templates" },
  { key: "sent", label: "Sent log" },
  { key: "optouts", label: "Opt-outs" },
];

const mailtoHref = (item) =>
  `mailto:${encodeURIComponent(item.email)}?subject=${encodeURIComponent(item.subject)}&body=${encodeURIComponent(item.mailto_text)}`;

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6);
const hourLabel = (h) => (h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// ---- settings ----
const SettingsCard = ({ settings, onSaved, forceOpen }) => {
  const { api, onAuthError } = useSession();
  const missingAddress = !settings.postal_address;
  const [open, setOpen] = useState(missingAddress || forceOpen);
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { hard_daily_max: _max, ...body } = form;
      const res = await api.put("/outreach/settings", { ...body, daily_limit: Number(form.daily_limit) || 1,
        send_start_hour: Number(form.send_start_hour), send_end_hour: Number(form.send_end_hour) });
      onSaved(res.data);
      toast.success("Outreach settings saved.");
    } catch (e) {
      onAuthError(e, "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${card} mb-6`} data-testid="outreach-settings">
      {missingAddress && (
        <div className="flex items-start gap-3 border-b border-amber-400/30 bg-amber-400/10 px-5 py-4 text-sm text-amber-200" data-testid="outreach-address-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>
            Add your business postal address before sending. The law (CAN-SPAM) requires a real mailing address
            in every marketing email; nothing will send until it is filled in. A PO box or registered mailbox works.
          </p>
        </div>
      )}
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <SettingsIcon size={15} className="text-vortex-cyan" />
        <span className="text-sm text-slate-200">Sending settings</span>
        <span className="ml-2 truncate text-xs text-slate-500">
          {settings.sender_name} · cap {settings.daily_limit}/day · {!settings.enabled ? "off" : settings.auto_send
            ? `auto ${hourLabel(settings.send_start_hour)}–${hourLabel(settings.send_end_hour)}` : "on"}
        </span>
        <span className="ml-auto text-slate-500">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </button>
      {open && (
        <div className="border-t border-white/5 px-5 pb-5 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <p className={label}>Postal address (shown in every email)</p>
              <Input className={`mt-2 ${field} ${missingAddress ? "border-amber-400/60" : ""}`} value={form.postal_address || ""}
                onChange={set("postal_address")} placeholder="Street or PO Box, West Chester, PA 19380" data-testid="outreach-postal-address" />
            </div>
            <div>
              <p className={label}>Your name (signature)</p>
              <Input className={`mt-2 ${field}`} value={form.sender_name || ""} onChange={set("sender_name")} />
            </div>
            <div>
              <p className={label}>Phone (signature)</p>
              <Input className={`mt-2 ${field}`} value={form.sender_phone || ""} onChange={set("sender_phone")} />
            </div>
            <div>
              <p className={label}>From address</p>
              <Input className={`mt-2 ${field}`} value={form.from_email || ""} onChange={set("from_email")} />
              <p className="mt-1 text-[11px] text-slate-500">Must be @vortexvs.com. For now emails go out from the site's usual sender; replies go to the reply-to below.</p>
            </div>
            <div>
              <p className={label}>Reply-to</p>
              <Input className={`mt-2 ${field}`} value={form.reply_to || ""} onChange={set("reply_to")} />
            </div>
            <div>
              <p className={label}>Daily limit (max {settings.hard_daily_max})</p>
              <Input type="number" min={1} max={settings.hard_daily_max} className={`mt-2 ${field}`} value={form.daily_limit}
                onChange={set("daily_limit")} />
              <p className="mt-1 text-[11px] text-slate-500">
                Resend's plan allows 100 emails a day across everything, including your sign-in codes. Keep room for those.
              </p>
            </div>
            <label className="flex items-center gap-3 self-start pt-6 text-sm text-slate-300">
              <input type="checkbox" checked={!!form.enabled} onChange={set("enabled")} className="h-4 w-4 accent-[#5CB3E2]" />
              Sending turned on
            </label>
            <div className="sm:col-span-2 rounded-md border border-white/10 bg-vortex-ink/40 p-4" data-testid="outreach-auto-settings">
              <label className="flex items-center gap-3 text-sm text-slate-200">
                <input type="checkbox" checked={!!form.auto_send} onChange={set("auto_send")} className="h-4 w-4 accent-[#5CB3E2]"
                  data-testid="outreach-auto-send" />
                Send automatically, spread through the day
              </label>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                Between
                <select aria-label="Start sending at" className={selectCls} value={form.send_start_hour} onChange={set("send_start_hour")}>
                  {HOURS.filter((h) => h <= 20).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
                and
                <select aria-label="Stop sending at" className={selectCls} value={form.send_end_hour} onChange={set("send_end_hour")}>
                  {HOURS.filter((h) => h >= 7).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </select>
                Eastern
                <label className="ml-2 flex items-center gap-2">
                  <input type="checkbox" checked={!!form.weekdays_only} onChange={set("weekdays_only")} className="h-4 w-4 accent-[#5CB3E2]" />
                  Weekdays only
                </label>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Up to your daily limit goes out one at a time, evenly spaced, follow-ups first, then the best leads.
                Each intro email gets a line about that place. You can still edit any email below before its turn.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={save} disabled={saving} className={btnPrimary}><Save size={14} /> {saving ? "Saving" : "Save settings"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- due today ----
const QueueRow = ({ item, checked, onCheck, onSend, onLog, onAction, onDraft, busy, canSend }) => {
  const [open, setOpen] = useState(false);
  const [mailed, setMailed] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body} while editing this lead's email
  const saveDraft = () => onDraft(draft).then((ok) => ok && setDraft(null));
  return (
    <div className="border-b border-white/5 px-5 py-4 last:border-b-0" data-testid={`outreach-row-${item.lead_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} aria-label={`Select ${item.company}`}
          className="h-4 w-4 accent-[#5CB3E2]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/admin/crm?lead=${item.lead_id}`} className="truncate text-sm font-medium text-slate-100 hover:text-vortex-cyan">{item.company}</Link>
            <Pill className={STEP_TONE[item.step]}>Step {item.step} · {STEP_LABEL[item.step]}</Pill>
            {item.custom && <Pill className="border-vortex-cyan/50 text-vortex-cyan">Edited</Pill>}
            {!item.custom && item.hook && <Pill className="border-emerald-400/50 text-emerald-300" title={item.hook}>Personal line</Pill>}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {[item.contact_name, item.email, item.city].filter(Boolean).join(" · ")}
            {item.last_sent_at && ` · last email ${fmt(item.last_sent_at)}`}
          </p>
          <button onClick={() => setOpen((v) => !v)} className="mt-1 flex max-w-full items-center gap-1 text-left text-xs text-slate-400 hover:text-vortex-cyan">
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            <span className="truncate">{item.subject}</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onSend} disabled={busy || !canSend} className={rowBtn} title={canSend ? "Send now through the site" : "Sending is blocked: see settings"}>
            <Send size={12} /> Send
          </button>
          {mailed ? (
            <button onClick={() => onLog().then((ok) => ok && setMailed(false))} disabled={busy} className={`${rowBtn} border-vortex-cyan/50 text-vortex-cyan`}>
              <MailCheck size={12} /> Log as sent
            </button>
          ) : (
            <a href={mailtoHref(item)} onClick={() => setMailed(true)} className={rowBtn}><Mail size={12} /> Open in my email</a>
          )}
          <button onClick={() => onAction("skip")} disabled={busy} className={rowBtn} title="Hide until tomorrow"><SkipForward size={12} /> Skip</button>
          <button onClick={() => onAction("replied")} disabled={busy} className={rowBtn} title="Stop the sequence for this lead"><CheckCircle2 size={12} /> Replied</button>
        </div>
      </div>
      {open && (draft ? (
        <div className="mt-3 rounded-md border border-vortex-cyan/30 bg-vortex-ink/60 p-4" data-testid="outreach-draft-editor">
          <p className={label}>Subject</p>
          <Input className={`mt-2 ${field}`} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          <p className={`${label} mt-4`}>Body</p>
          <Textarea rows={12} className={`mt-2 ${field}`} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={saveDraft} disabled={busy || !draft.subject.trim() || !draft.body.trim()} className={rowBtn}><Save size={12} /> Save for this lead</button>
            <button onClick={() => setDraft(null)} className={rowBtn}>Cancel</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Used for this email only. Later steps go back to your templates.</p>
        </div>
      ) : (
        <div className="mt-3 rounded-md border border-white/10 bg-vortex-ink/60 p-4">
          <p className={label}>Subject</p>
          <p className="mt-1 text-sm text-slate-200">{item.subject}</p>
          <p className={`${label} mt-4`}>Body</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{item.text}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button onClick={() => setDraft({ subject: item.subject, body: item.text })} className={rowBtn} data-testid="outreach-edit-draft">
              <Pencil size={12} /> Edit for this lead
            </button>
            {item.custom && <button onClick={() => onDraft(null)} disabled={busy} className={rowBtn}><RotateCcw size={12} /> Use template</button>}
          </div>
          <p className="mt-3 text-[11px] text-slate-500">Sent emails also get a footer with your postal address and an unsubscribe link.</p>
        </div>
      ))}
    </div>
  );
};

const QueueTab = ({ queue, reload, onSettingsNeeded }) => {
  const { api, onAuthError } = useSession();
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const items = queue.items;
  const pickedIds = items.filter((i) => picked[i.lead_id]).map((i) => i.lead_id);
  const allPicked = items.length > 0 && pickedIds.length === items.length;

  const send = async (ids) => {
    if (!queue.ready) { onSettingsNeeded(); return; }
    if (ids.length > queue.remaining_today) {
      toast.error(`Only ${queue.remaining_today} more can go out today. Select fewer.`);
      return;
    }
    if (ids.length > 1 && !window.confirm(`Send ${ids.length} emails now? They go out about a second apart.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/outreach/send", { lead_ids: ids });
      const failed = res.data.results.filter((r) => !r.ok);
      if (res.data.sent) toast.success(`Sent ${res.data.sent} email${res.data.sent === 1 ? "" : "s"}. ${res.data.remaining_today} left today.`);
      if (failed.length) toast.error(failed[0].error + (failed.length > 1 ? ` (+${failed.length - 1} more not sent)` : ""));
      setPicked({});
    } catch (e) {
      onAuthError(e, "Could not send.");
    } finally {
      setBusy(false);
      reload();
    }
  };

  const log = async (item) => {
    setBusy(true);
    try {
      await api.post("/outreach/log", { lead_id: item.lead_id, step: item.step });
      toast.success(`Logged email ${item.step} to ${item.company}.`);
      reload();
      return true;
    } catch (e) {
      onAuthError(e, "Could not log the email.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async (item, draft) => {
    setBusy(true);
    try {
      if (draft) {
        const res = await api.put(`/outreach/leads/${item.lead_id}/draft`, draft);
        toast.success(`Saved your email for ${item.company}.`);
        if (res.data.warnings?.length) toast.warning(res.data.warnings.join(" "));
      } else {
        await api.delete(`/outreach/leads/${item.lead_id}/draft`);
      }
      reload();
      return true;
    } catch (e) {
      onAuthError(e, "Could not save the email.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const act = async (item, action) => {
    setBusy(true);
    try {
      await api.post(`/outreach/leads/${item.lead_id}/action`, { action });
      toast.success(action === "skip" ? `${item.company} skipped until tomorrow.` : `${item.company}: sequence stopped.`);
      reload();
    } catch (e) {
      onAuthError(e, "Could not update the lead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={card} data-testid="outreach-queue">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/5 px-5 py-4">
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={allPicked} disabled={!items.length} aria-label="Select all"
            onChange={(e) => setPicked(e.target.checked ? Object.fromEntries(items.map((i) => [i.lead_id, true])) : {})}
            className="h-4 w-4 accent-[#5CB3E2]" />
          {pickedIds.length ? `${pickedIds.length} selected` : `${items.length} due`}
        </label>
        <span className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
          Sent today {queue.sent_today}/{queue.daily_limit} · {queue.remaining_today} left
        </span>
        <button onClick={() => send(pickedIds)} disabled={busy || !pickedIds.length || !queue.remaining_today}
          className={`${btnPrimary} ml-auto px-5 py-2`} data-testid="outreach-send-selected">
          <Send size={13} /> Send selected{pickedIds.length ? ` (${Math.min(pickedIds.length, queue.remaining_today)})` : ""}
        </button>
      </div>
      {queue.auto && (
        <div className={`flex items-start gap-2 border-b border-white/5 px-5 py-3 text-xs ${queue.auto.paused_error ? "text-amber-300" : "text-slate-400"}`} data-testid="outreach-auto-status">
          <Clock size={13} className="mt-0.5 shrink-0" />
          {queue.auto.paused_error ? `Auto-send paused until tomorrow: ${queue.auto.paused_error}`
            : queue.auto.on ? `Auto-send is on: up to ${queue.daily_limit} a day, spread between ${hourLabel(queue.auto.start_hour)} and ${hourLabel(queue.auto.end_hour)}${queue.auto.weekdays_only ? " on weekdays" : ""}, starting from the top of this list.`
            : <span>Auto-send is off. Turn it on in <button className="text-vortex-cyan hover:underline" onClick={onSettingsNeeded}>Sending settings</button> to send through the day without pressing Send.</span>}
        </div>
      )}
      {items.length === 0 ? (
        <p className="px-5 py-10 text-sm text-slate-500">
          Nothing due today.{queue.upcoming ? ` ${queue.upcoming} follow-up${queue.upcoming === 1 ? " is" : "s are"} scheduled for later days.` : ""}{" "}
          New and contacted leads with an email address show up here. <Link to="/admin/crm" className="text-vortex-cyan hover:underline">Find leads</Link>
        </p>
      ) : items.map((item) => (
        <QueueRow key={item.lead_id} item={item} busy={busy} canSend={queue.ready && queue.remaining_today > 0}
          checked={!!picked[item.lead_id]} onCheck={(v) => setPicked((p) => ({ ...p, [item.lead_id]: v }))}
          onSend={() => send([item.lead_id])} onLog={() => log(item)} onAction={(a) => act(item, a)}
          onDraft={(d) => saveDraft(item, d)} />
      ))}
    </div>
  );
};

// ---- templates ----
const TemplatesTab = () => {
  const { api, onAuthError } = useSession();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("generic");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    api.get("/outreach/templates").then((r) => { setData(r.data); setDrafts({}); })
      .catch((e) => onAuthError(e, "Could not load templates."));
  }, [api, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const byKey = useMemo(() => Object.fromEntries((data?.templates || []).map((t) => [`${t.category}:${t.step}`, t])), [data]);
  if (!data) return <p className="text-sm text-slate-500">Loading templates...</p>;

  const own = (step) => byKey[`${category}:${step}`];
  const shown = (step) => drafts[step] || own(step) || byKey[`generic:${step}`] || { subject: "", body: "" };
  const edit = (step, key) => (e) => setDrafts((d) => ({ ...d, [step]: { ...shown(step), [key]: e.target.value } }));

  const save = async (step) => {
    setSaving(step);
    try {
      const { subject, body } = shown(step);
      const res = await api.put(`/outreach/templates/${category}/${step}`, { subject, body });
      toast.success(`Step ${step} saved.`);
      if (res.data.warnings?.length) toast.warning(res.data.warnings.join(" "));
      load();
    } catch (e) {
      onAuthError(e, "Could not save the template.");
    } finally {
      setSaving(null);
    }
  };

  const reset = async (step) => {
    const msg = category === "generic" ? "Reset this step to the original wording?" : "Remove this category's version and use the general one?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/outreach/templates/${category}/${step}`);
      load();
    } catch (e) {
      onAuthError(e, "Could not reset the template.");
    }
  };

  return (
    <div data-testid="outreach-templates">
      <div className={`${card} mb-4 flex flex-wrap items-center gap-3 px-5 py-4`}>
        <p className={label}>Category</p>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setDrafts({}); }} className={selectCls} aria-label="Template category">
          {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <p className="text-xs text-slate-500">
          Merge fields: {data.merge_fields.map((f) => `{${f}}`).join(" ")}. Keep it short and plain: no "free" claims,
          testimonials, ratings or statistics, and one clear ask.
        </p>
      </div>
      {category !== "generic" && data.pitches[category] && (
        <p className="mb-4 text-xs text-slate-500">{"{pitch}"} for this category: <span className="text-slate-300">{data.pitches[category]}</span></p>
      )}
      <div className="grid gap-4">
        {data.steps.map(({ step, label: stepLabel, delay_days: delay }) => {
          const tpl = shown(step);
          const inherited = category !== "generic" && !own(step) && !drafts[step];
          const warnings = own(step)?.warnings || (category === "generic" ? [] : byKey[`generic:${step}`]?.warnings) || [];
          return (
            <div key={step} className={`${card} p-5`}>
              <div className="flex flex-wrap items-center gap-3">
                <Pill className={STEP_TONE[step]}>Step {step} · {stepLabel}</Pill>
                <span className="text-xs text-slate-500">{delay ? `${delay} days after step ${step - 1}` : "Day 0"}</span>
                {inherited && <span className="text-xs text-slate-500">Using the general version. Edit to make one for this category.</span>}
              </div>
              <p className={`${label} mt-4`}>Subject</p>
              <Input className={`mt-2 ${field}`} value={tpl.subject} onChange={edit(step, "subject")} />
              <p className={`${label} mt-4`}>Body</p>
              <Textarea className={`mt-2 min-h-[220px] font-mono text-xs leading-relaxed ${field}`} value={tpl.body} onChange={edit(step, "body")} />
              {warnings.length > 0 && <p className="mt-2 text-xs text-amber-300">{warnings.join(" ")}</p>}
              <div className="mt-4 flex justify-end gap-2">
                {(own(step) || category === "generic") && (
                  <button onClick={() => reset(step)} className={rowBtn}><RotateCcw size={12} /> {category === "generic" ? "Reset" : "Use general"}</button>
                )}
                <button onClick={() => save(step)} disabled={saving === step || !drafts[step]} className={rowBtn}><Save size={12} /> Save</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- sent log ----
const SentTab = () => {
  const { api, onAuthError } = useSession();
  const [sends, setSends] = useState(null);
  useEffect(() => {
    api.get("/outreach/sends").then((r) => setSends(r.data)).catch((e) => onAuthError(e, "Could not load sent emails."));
  }, [api, onAuthError]);
  return (
    <div className={card} data-testid="outreach-sent">
      {sends === null ? <p className="px-5 py-10 text-sm text-slate-500">Loading...</p>
        : sends.length === 0 ? <p className="px-5 py-10 text-sm text-slate-500">No outreach emails yet.</p>
        : sends.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 border-b border-white/5 px-5 py-3 last:border-b-0">
            <Pill className={STEP_TONE[s.step]}>Step {s.step}</Pill>
            <div className="min-w-0 flex-1">
              <Link to={`/admin/crm?lead=${s.lead_id}`} className="text-sm text-slate-100 hover:text-vortex-cyan">{s.company || s.email}</Link>
              <p className="truncate text-xs text-slate-500">{s.email} · {s.subject}</p>
            </div>
            <span className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
              {s.channel === "mailto" ? "My email" : "Sent"} · {fmt(s.sent_at)}
            </span>
          </div>
        ))}
    </div>
  );
};

// ---- opt-outs ----
const OptoutsTab = () => {
  const { api, onAuthError } = useSession();
  const [rows, setRows] = useState(null);
  const [value, setValue] = useState("");
  const load = useCallback(() => {
    api.get("/outreach/optouts").then((r) => setRows(r.data)).catch((e) => onAuthError(e, "Could not load opt-outs."));
  }, [api, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/outreach/optouts", { value });
      setValue("");
      toast.success("Added. They won't get outreach emails.");
      load();
    } catch (err) {
      onAuthError(err, "Could not add.");
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.value} from the do-not-email list?`)) return;
    try { await api.delete(`/outreach/optouts/${row.id}`); load(); } catch (err) { onAuthError(err, "Could not remove."); }
  };

  return (
    <div className={card} data-testid="outreach-optouts">
      <form onSubmit={add} className="flex flex-wrap items-center gap-3 border-b border-white/5 px-5 py-4">
        <Input className={`max-w-xs ${field}`} value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@company.com or company.com" />
        <button type="submit" disabled={!value.trim()} className={rowBtn}><Ban size={12} /> Do not email</button>
        <p className="text-xs text-slate-500">Unsubscribe clicks land here automatically. A domain blocks everyone at that company.</p>
      </form>
      {rows === null ? <p className="px-5 py-8 text-sm text-slate-500">Loading...</p>
        : rows.length === 0 ? <p className="px-5 py-8 text-sm text-slate-500">No opt-outs yet.</p>
        : rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 border-b border-white/5 px-5 py-3 last:border-b-0">
            <span className="text-sm text-slate-200">{r.value}</span>
            <span className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
              {r.source === "unsubscribe_link" ? "Unsubscribed" : "Added by you"} · {fmt(r.at)}
            </span>
            <button onClick={() => remove(r)} className="ml-auto text-slate-500 hover:text-red-300" aria-label={`Remove ${r.value}`}><Trash2 size={14} /></button>
          </div>
        ))}
    </div>
  );
};

// Cold outreach email: a 3-step sequence to new and contacted leads, capped per day.
const Outreach = () => {
  const { api, onAuthError } = useSession();
  const [tab, setTab] = useState("queue");
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState(null);
  const [settingsKey, setSettingsKey] = useState(0);

  const loadQueue = useCallback(() => {
    api.get("/outreach/queue").then((r) => setQueue(r.data)).catch((e) => onAuthError(e, "Could not load the outreach queue."));
  }, [api, onAuthError]);
  const loadSettings = useCallback(() => {
    api.get("/outreach/settings").then((r) => setSettings(r.data)).catch((e) => onAuthError(e, "Could not load outreach settings."));
  }, [api, onAuthError]);
  useEffect(() => { loadSettings(); loadQueue(); }, [loadSettings, loadQueue]);

  const settingsNeeded = () => {
    toast.error(queue?.problems?.[0] || "Check your outreach settings first.");
    setSettingsKey((k) => k + 1);  // remount the card so it opens
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="outreach-page">
      <PageHeader title="Outreach"
        intro="Cold emails to new leads in three short steps: an intro, a follow-up 3 days later, and a final note a week after that. Replies come to your inbox; mark them here to stop the sequence."
        actions={<>
          <button onClick={() => { loadSettings(); loadQueue(); }} className={ghostBtn}><RefreshCw size={14} /> Refresh</button>
          <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className={ghostBtn}><ExternalLink size={14} /> Resend</a>
        </>} />

      {settings && <SettingsCard key={settingsKey} forceOpen={settingsKey > 0} settings={settings} onSaved={(s) => { setSettings(s); loadQueue(); }} />}

      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`rounded-full border px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${tab === t.key
              ? "border-vortex-cyan bg-vortex-cyan/10 text-vortex-cyan" : "border-white/15 text-slate-400 hover:text-vortex-cyan"}`}>
            {t.label}{t.key === "queue" && queue ? ` (${queue.items.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "queue" && (queue === null
        ? <p className="text-sm text-slate-500">Loading...</p>
        : <QueueTab queue={queue} reload={loadQueue} onSettingsNeeded={settingsNeeded} />)}
      {tab === "templates" && <TemplatesTab />}
      {tab === "sent" && <SentTab />}
      {tab === "optouts" && <OptoutsTab />}
    </div>
  );
};

export default Outreach;
